import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  HardHat, ArrowRight, ArrowLeft, Search, ShieldCheck, Users, BarChart3,
  Award, ClipboardCheck, Filter, Star, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const fadeUp = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.5 },
};

const features = [
  { icon: Search, t: "Vendor discovery", d: "Search a directory of contractors by readiness score, certifications, bonding capacity, trade, and region." },
  { icon: Award, t: "AI shortlisting", d: "Auto-rank the best-qualified contractors for any solicitation — the win-probability engine, pointed your way." },
  { icon: ShieldCheck, t: "Compliance at a glance", d: "Instantly verify licensing, insurance, and bonding before you award the work." },
  { icon: Users, t: "Diversity & set-asides", d: "Surface qualified MBE, WBE, SDVOSB, and HUBZone firms and report against your goals." },
  { icon: ClipboardCheck, t: "Response management", d: "Receive, compare, and score incoming bids in one organized workspace." },
  { icon: BarChart3, t: "Sourcing analytics", d: "Track your applicant funnel, time-to-award, and supplier diversity mix." },
];

const inputCls = "rounded-md border border-slate-200 px-3.5 py-2.5 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40 focus:border-emerald-500";

const directory = [
  { name: "Summit Mechanical", trade: "HVAC", score: 842, certs: ["MBE", "8(a)"], loc: "Denver, CO" },
  { name: "Ironline Electric", trade: "Electrical", score: 781, certs: ["SDVOSB"], loc: "San Antonio, TX" },
  { name: "Keystone Civil Works", trade: "Civil", score: 905, certs: ["WBE", "ISO 9001"], loc: "Columbus, OH" },
];

export default function ForAgencies() {
  const { user, login } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", organization: "", org_type: "agency", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.includes("@")) { toast.error("Please enter a valid email"); return; }
    setSubmitting(true);
    try {
      await api.post("/agency-waitlist", form);
      setDone(true);
      toast.success("You're on the list — we'll be in touch!");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#fafdfb] text-slate-900 font-sans antialiased">
      {/* Nav */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="agencies-home-link">
            <div className="h-8 w-8 rounded-lg bg-emerald-900 flex items-center justify-center">
              <HardHat className="h-4 w-4 text-lime-300" strokeWidth={2.2} />
            </div>
            <span className="font-heading font-bold text-lg tracking-tight">BidMatchAI</span>
          </a>
          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
            <a href="/" className="hover:text-slate-900 transition-colors">Home</a>
            <a href="/platform" className="hover:text-slate-900 transition-colors">Why BidMatchAI</a>
            <a href="/#pricing" className="hover:text-slate-900 transition-colors">Pricing</a>
          </nav>
          <a href="#waitlist" data-testid="agencies-nav-cta" className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-md px-5 py-2.5 text-sm font-medium transition-all shadow-sm hover:-translate-y-0.5">Request access</a>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 -z-10">
          <div className="absolute -top-24 right-10 h-[420px] w-[420px] rounded-full bg-lime-300/30 blur-[120px]" />
          <div className="absolute top-32 -left-20 h-[380px] w-[380px] rounded-full bg-emerald-400/25 blur-[120px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 lg:py-28">
          <motion.div {...fadeUp} className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald-700 mb-6">
            <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-lime-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-lime-500" /></span>
            For Issuing Organizations &amp; Owners · Early access
          </motion.div>
          <motion.h1 {...fadeUp} className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[0.98] max-w-3xl">
            Award work to contractors you can trust.
          </motion.h1>
          <motion.p {...fadeUp} transition={{ duration: 0.5, delay: 0.1 }} className="mt-6 text-lg text-slate-600 max-w-2xl">
            Post your opportunity and instantly reach pre-qualified contractors — ranked by readiness, certifications, and compliance. Spend less time vetting, more time building.
          </motion.p>
          <motion.div {...fadeUp} transition={{ duration: 0.5, delay: 0.2 }} className="mt-8 flex flex-wrap gap-3">
            <a href="#waitlist" data-testid="hero-waitlist-cta" className="group bg-emerald-900 text-white hover:bg-emerald-800 rounded-md px-7 py-3.5 font-medium transition-all shadow-sm hover:-translate-y-0.5 inline-flex items-center gap-2">
              Request early access <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </a>
            <a href="#preview" className="rounded-md px-7 py-3.5 font-medium border border-slate-300 text-slate-700 hover:border-slate-400 transition-colors">See a preview</a>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <motion.div {...fadeUp} className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">What you'll be able to do</p>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Sourcing, vetting, and awarding — in one place.</h2>
        </motion.div>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f) => (
            <motion.div key={f.t} {...fadeUp} data-testid={`agency-feature-${f.t}`} className="rounded-2xl border border-slate-200 bg-white p-7 hover:shadow-sm hover:border-emerald-200 transition-all">
              <div className="h-11 w-11 rounded-lg bg-emerald-50 flex items-center justify-center"><f.icon className="h-5 w-5 text-emerald-700" /></div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{f.t}</h3>
              <p className="mt-2 text-sm text-slate-600">{f.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* MVP preview */}
      <section id="preview" className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24">
          <motion.div {...fadeUp} className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-lime-400">A peek at the issuing-organization workspace</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold tracking-tight">Find your shortlist in minutes, not weeks.</h2>
          </motion.div>

          <motion.div {...fadeUp} className="mt-12 rounded-2xl bg-white text-slate-900 shadow-2xl overflow-hidden">
            <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50">
              <span className="h-3 w-3 rounded-full bg-red-300" /><span className="h-3 w-3 rounded-full bg-amber-300" /><span className="h-3 w-3 rounded-full bg-emerald-300" />
              <span className="ml-3 text-xs font-data text-slate-400">app.bidmatchai.io / organization / vendor-search</span>
            </div>
            <div className="grid lg:grid-cols-12 gap-6 p-6 md:p-8 bg-[#fafdfb]">
              {/* filters */}
              <div className="lg:col-span-3 rounded-xl border border-slate-200 bg-white p-5">
                <p className="flex items-center gap-2 text-sm font-semibold"><Filter className="h-4 w-4 text-emerald-700" /> Filters</p>
                <div className="mt-4 space-y-3 text-sm">
                  <div><p className="text-xs text-slate-400 mb-1">Trade</p><div className="rounded-md border border-slate-200 px-3 py-2 text-slate-700">Mechanical / HVAC</div></div>
                  <div><p className="text-xs text-slate-400 mb-1">Min. readiness</p><div className="rounded-md border border-slate-200 px-3 py-2 text-slate-700">700+</div></div>
                  <div><p className="text-xs text-slate-400 mb-1">Certifications</p><div className="flex flex-wrap gap-1.5">{["MBE", "8(a)", "SDVOSB"].map((c) => <span key={c} className="text-xs rounded-full bg-emerald-50 text-emerald-700 px-2 py-0.5 border border-emerald-100">{c}</span>)}</div></div>
                </div>
              </div>
              {/* results */}
              <div className="lg:col-span-9 rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-center justify-between"><p className="text-sm font-semibold">Matched contractors</p><span className="text-xs text-slate-400 font-data">3 of 128</span></div>
                <div className="mt-3 divide-y divide-slate-100">
                  {directory.map((d) => (
                    <div key={d.name} className="flex items-center justify-between gap-4 py-3.5">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-slate-800 truncate">{d.name}</p>
                        <p className="text-xs text-slate-400">{d.trade} · {d.loc}</p>
                        <div className="mt-1.5 flex flex-wrap gap-1.5">{d.certs.map((c) => <span key={c} className="text-[10px] rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{c}</span>)}</div>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        <div className="text-right"><p className="font-data text-lg font-semibold text-emerald-700 leading-none flex items-center gap-1"><Star className="h-3.5 w-3.5 fill-emerald-600 text-emerald-600" />{d.score}</p><p className="text-[10px] uppercase tracking-wider text-slate-400">readiness</p></div>
                        <span className="text-xs font-semibold rounded-md bg-emerald-900 text-white px-3 py-1.5">Shortlist</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Waitlist */}
      <section id="waitlist" className="max-w-3xl mx-auto px-6 md:px-12 py-24">
        <motion.div {...fadeUp} className="text-center">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Be first to source on BidMatchAI.</h2>
          <p className="mt-3 text-slate-600">The issuing-organization side is in early access. Join the waitlist and we'll reach out as we onboard.</p>
        </motion.div>

        {done ? (
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} data-testid="waitlist-success" className="mt-10 rounded-2xl border border-emerald-200 bg-emerald-50 p-10 text-center">
            <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
            <h3 className="mt-4 font-heading text-xl font-bold">You're on the list!</h3>
            <p className="mt-2 text-slate-600">Thanks{form.name ? `, ${form.name.split(" ")[0]}` : ""} — we'll email you at <span className="font-medium text-slate-900">{form.email}</span> when issuing-organization access opens.</p>
          </motion.div>
        ) : (
          <motion.form {...fadeUp} onSubmit={submit} data-testid="waitlist-form" className="mt-10 rounded-2xl border border-slate-200 bg-white p-7 shadow-sm space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <label className="flex flex-col gap-1.5"><span className="text-sm font-medium text-slate-700">Your name</span><input className={inputCls} data-testid="waitlist-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" /></label>
              <label className="flex flex-col gap-1.5"><span className="text-sm font-medium text-slate-700">Work email *</span><input type="email" className={inputCls} data-testid="waitlist-email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@agency.gov" required /></label>
              <label className="flex flex-col gap-1.5"><span className="text-sm font-medium text-slate-700">Organization</span><input className={inputCls} data-testid="waitlist-org" value={form.organization} onChange={(e) => set("organization", e.target.value)} placeholder="City of…, Prime Co., etc." /></label>
              <label className="flex flex-col gap-1.5"><span className="text-sm font-medium text-slate-700">You are a…</span>
                <select className={inputCls} data-testid="waitlist-type" value={form.org_type} onChange={(e) => set("org_type", e.target.value)}>
                  <option value="agency">Government / issuing organization</option>
                  <option value="prime">Prime contractor</option>
                  <option value="owner">Project owner / developer</option>
                  <option value="other">Other</option>
                </select>
              </label>
            </div>
            <label className="flex flex-col gap-1.5"><span className="text-sm font-medium text-slate-700">What do you need to source? (optional)</span><textarea className={`${inputCls} min-h-[90px]`} data-testid="waitlist-message" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about the work you award…" /></label>
            <button type="submit" disabled={submitting} data-testid="waitlist-submit" className="w-full bg-emerald-900 text-white rounded-md py-3 font-medium hover:bg-emerald-800 transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
              {submitting ? "Submitting…" : "Join the waitlist"} <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-xs text-slate-400 text-center">No spam. We'll only email you about issuing-organization access.</p>
          </motion.form>
        )}
      </section>

      <footer className="bg-emerald-950 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 flex items-center justify-between">
          <a href="/" className="text-sm text-emerald-200 hover:text-white transition-colors inline-flex items-center gap-2"><ArrowLeft className="h-4 w-4" /> Back to home</a>
          <span className="text-sm text-emerald-300/70">© 2026 BidMatchAI</span>
        </div>
      </footer>
    </div>
  );
}
